/**
 * PromotionListPresenter
 * --
 */
import React from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  Text,
  ModalLayout,
} from 'components';
import {
  Button,
  DatePicker,
  Table,
  Input,
  Select,
  Spin,
  Image,
  Flex,
  Space,
  Tag,
  Radio,
} from 'antd';
import { stringToMoneyFormat, STORE_URL } from 'utils';
import dayjs from 'dayjs';
import { SearchOutlined } from '@ant-design/icons';
import {
  PROMOTION_TYPE,
  PROMOTION_YES_OR_NO,
} from 'constants/PromotionContants';
import PromotionRegisterContainer from '../PromotionRegister';
import { downloadXlsx } from 'utils/XlsxDownloader';
/**----------------------------------------
 * [Component] 총관리자: 프로모션 > 프레젠터
 * ----------------------------------------
 */
const PromotionListPresenter = (props) => {
  /* ===== ROUTE ===== */

  /* ===== PROPS ===== */
  const {
    isLoading = false,
    setIsLoading = () => {},
    promotionList,
    selectedList,
    onSelect = () => {},
    onSort,
    sortItems,
    isOpened = false,
    onOpen = () => {},
    currentShop,
    dateValue,
    setDateValue = () => {},
    setPeriod = () => {},
    INITIAL_VALUE,
    onSubmit = () => {},
    onDelete = () => {},
  } = props;

  /* ===== STATE ===== */
  const searchInput = React.useRef(null);
  const [searchText, setSearchText] = React.useState('');
  const [searchedColumn, setSearchedColumn] = React.useState('');
  const [modalType, setModalType] = React.useState(false);
  const [modalData, setModalData] = React.useState(null);
  /* ===== FUNCTION ===== */
  const handleTableSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleTableReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText('');
    confirm();
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleTableSearch(selectedKeys, confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleTableSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleTableReset(clearFilters, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex.replace(/^processed_/, '').replace(/^expired_/, '')]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });
  /* ===== VARIABLE ===== */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 20,
    },
    button: {
      width: 80,
      height: 40,
      fontSize: 15,
      fontWeight: 600,
      margin: '0px 4px',
    },
    form: {
      input: {
        large: {
          width: 175,
          marginRight: 8,
        },
        medium: {
          width: 100,
          marginRight: 8,
        },
      },
      button: {
        marginRight: 8,
      },
      date: {
        width: 120,
      },
      text: {
        fontSize: 13,
        fontWeight: 400,
      },
    },
  };

  const formItem = [
    {
      label: '프로모션 기간',
      form: [
        {
          input: (
            <Flex gap="large" size="large">
              <Radio.Group
                size="large"
                value={dateValue.value}
                onChange={(e) =>
                  setDateValue((prev) => ({
                    ...prev,
                    value: e.target.value,
                    start_date: dayjs()
                      .subtract(e.target.value, 'day')
                      .startOf('day'),
                    end_date: dayjs().endOf('day'),
                  }))
                }
              >
                <Radio.Button value={1}>1일</Radio.Button>
                <Radio.Button value={7}>7일</Radio.Button>
                <Radio.Button value={30}>30일</Radio.Button>
                <Radio.Button value={90}>90일</Radio.Button>
              </Radio.Group>
              <Flex align="center" gap={4}>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dateValue.start_date}
                  onChange={(e) =>
                    setDateValue((prev) => ({
                      ...prev,
                      start_date: dayjs(e).startOf('day'),
                    }))
                  }
                  maxDate={dateValue.end_date}
                />
                <p style={{ ...styles.form.text, padding: '0px 2px' }}>~</p>
                <DatePicker
                  size="large"
                  format={'YYYY-MM-DD'}
                  placeholder=""
                  style={{ ...styles.form.date, width: 150 }}
                  value={dateValue.end_date}
                  onChange={(e) =>
                    setDateValue((prev) => ({
                      ...prev,
                      end_date: dayjs(e).endOf('day'),
                    }))
                  }
                  minDate={dateValue.start_date}
                />
              </Flex>
            </Flex>
          ),
        },
      ],
    },
  ];

  /**
   * 프로모션 컬럼
   * ---
   */
  const columns = React.useMemo(() => {
    return [
      {
        title: '프로모션 명',
        key: 'promotion_title',
        dataIndex: 'promotion_title',
        width: 200,
        ...getColumnSearchProps('promotion_title'),
        sorter: (a, b) =>
          a.promotion_title.localeCompare(b.promotion_title, 'ko'),
      },
      {
        title: '유형',
        key: 'promotion_type',
        dataIndex: 'promotion_type',
        width: 100,
        render: (promotion_type) => PROMOTION_TYPE[promotion_type].label,
        filters: Object.entries(PROMOTION_TYPE).map(([key, v]) => ({
          text: v.label,
          value: key,
        })),
        onFilter: (value, record) => record.promotion_type === value,
      },
      {
        title: '썸네일',
        dataIndex: 'thumbnail_url',
        key: 'thumbnail_url',
        width: 100,
        render: (thumbnail_url) => (
          <Image
            src={`${STORE_URL}/${thumbnail_url}`}
            width={80}
            height={50}
            preview={false}
          />
        ),
      },
      {
        title: '시작일',
        dataIndex: 'promotion_start_date',
        align: 'center',
        width: 160,
        render: (promotion_start_date) =>
          dayjs.unix(promotion_start_date).format('YYYY-MM-DD HH:mm'),
        sorter: (a, b) => a.promotion_start_date - b.promotion_start_date,
      },
      {
        title: '종료일',
        dataIndex: 'promotion_end_date',
        align: 'center',
        width: 160,
        render: (promotion_end_date) =>
          dayjs.unix(promotion_end_date).format('YYYY-MM-DD HH:mm'),
        sorter: (a, b) => a.promotion_end_date - b.promotion_end_date,
      },
      {
        title: '할인',
        dataIndex: 'discount_rate',
        align: 'center',
        width: 100,
        render: (discount_rate, record) =>
          `${stringToMoneyFormat(discount_rate)}${
            record.discount_type === 'W' ? '원' : '%'
          }`,
        sorter: (a, b) => a.discount_rate - b.discount_rate,
      },
      {
        title: '최대할인',
        dataIndex: 'max_discount_price',
        align: 'center',
        width: 100,
        render: (max_discount_price) =>
          `${stringToMoneyFormat(max_discount_price)}원`,
        sorter: (a, b) => a.max_discount_price - b.max_discount_price,
      },
      {
        title: '적립',
        dataIndex: 'reserve_rate',
        align: 'center',
        width: 100,
        render: (reserve_rate, record) =>
          `${stringToMoneyFormat(reserve_rate)}${
            record.reserve_type === 'W' ? '원' : '%'
          }`,
        sorter: (a, b) => a.reserve_rate - b.reserve_rate,
      },
      {
        title: '노출여부',
        dataIndex: 'is_show',
        align: 'center',
        width: 100,
        render: (is_show) => (
          <Tag color={is_show === 'Y' ? 'processing' : 'default'}>
            {PROMOTION_YES_OR_NO[is_show].SHOW.text}
          </Tag>
        ),
        filters: Object.entries(PROMOTION_YES_OR_NO).map(([key, v]) => ({
          text: v['SHOW'].text,
          value: key,
        })),
        onFilter: (value, record) => record.is_show === value,
      },
      {
        title: '메인노출여부',
        dataIndex: 'is_main',
        align: 'center',
        width: 130,
        render: (is_main) => (
          <Tag color={is_main === 'Y' ? 'processing' : 'default'}>
            {PROMOTION_YES_OR_NO[is_main].SHOW.text}
          </Tag>
        ),
        filters: Object.entries(PROMOTION_YES_OR_NO).map(([key, v]) => ({
          text: v['SHOW'].text,
          value: key,
        })),
        onFilter: (value, record) => record.is_show === value,
      },
      {
        title: '활성화여부',
        dataIndex: 'product_policy_is_show',
        align: 'center',
        width: 110,
        render: (product_policy_is_show) => (
          <Tag
            color={product_policy_is_show === 'Y' ? 'processing' : 'default'}
          >
            {PROMOTION_YES_OR_NO[product_policy_is_show].SHOW.text}
          </Tag>
        ),
        filters: Object.entries(PROMOTION_YES_OR_NO).map(([key, v]) => ({
          text: v['ENABLE'].text,
          value: key,
        })),
        onFilter: (value, record) => record.is_show === value,
      },
      {
        title: '순서',
        dataIndex: 'order',
        align: 'center',
        width: 100,
        render: (order) => order,
        sorter: (a, b) => a.order - b.order,
      },
      {
        title: '등록일',
        dataIndex: 'created_at',
        align: 'center',
        width: 200,
        render: (created_at) =>
          dayjs.unix(created_at).format('YYYY-MM-DD HH:mm'),
        sorter: (a, b) => a.created_at - b.created_at,
      },
    ];

    // eslint-disable-next-line
  }, [promotionList]);

  /**
   * 진행중, 종료 컬럼
   * ---
   */
  const sideDefaultColumns = [
    {
      title: '유형',
      key: 'promotion_type',
      dataIndex: 'promotion_type',
      render: (promotion_type) => PROMOTION_TYPE[promotion_type].label,
      filters: Object.entries(PROMOTION_TYPE).map(([key, v]) => ({
        text: v.label,
        value: key,
      })),
      onFilter: (value, record) => record.promotion_type === value,
    },
    {
      title: '시작일',
      key: 'promotion_start_date',
      dataIndex: 'promotion_start_date',
      render: (promotion_start_date) =>
        dayjs.unix(promotion_start_date).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => a.promotion_start_date - b.promotion_start_date,
    },
    {
      title: '종료일',
      key: 'promotion_end_date',
      dataIndex: 'promotion_end_date',
      render: (promotion_end_date) =>
        dayjs.unix(promotion_end_date).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => a.promotion_end_date - b.promotion_end_date,
    },
  ];
  const sideColumns = {
    PROCESS: [
      {
        title: '프로모션 명',
        key: 'promotion_title',
        dataIndex: 'promotion_title',
        ...getColumnSearchProps('processed_promotion_title'),
      },
      ...sideDefaultColumns,
    ],
    EXPIRED: [
      {
        title: '프로모션 명',
        key: 'promotion_title',
        dataIndex: 'promotion_title',
        ...getColumnSearchProps('expired_promotion_title'),
      },
      ...sideDefaultColumns,
    ],
  };

  /**
   * 아이템 순서 변경 (0-based index 기준)
   * @param {object} child - 순서를 변경할 대상 객체 (예: { present_card_id, order, ... })
   * @param {number} newIndex - 목표 인덱스 (0부터 시작)
   */
  const orderChange = (child, newIndex) => {
    // sortItems는 객체 배열이어야 합니다.
    // 우선 기존 배열의 복사본을 생성합니다.
    const newArr = [...sortItems];
    // 대상 객체의 현재 인덱스를 찾습니다.
    // banner_id나 다른 고유값을 기준으로 찾는 것을 권장합니다.
    const currentIndex = newArr.findIndex(
      (item) => item.promotion_id === child.promotion_id
    );
    if (currentIndex === -1) return; // 대상 객체를 찾지 못하면 종료
    if (currentIndex === newIndex) return; // 인덱스가 동일하면 변경할 필요 없음
    // 현재 인덱스에서 대상 아이템 제거
    const [movedItem] = newArr.splice(currentIndex, 1);
    // 목표 인덱스 위치에 대상 아이템 삽입
    newArr.splice(newIndex, 0, movedItem);

    // 배열 내의 모든 객체에 대해 order 값을 재할당 (1부터 시작하도록)
    const updatedList = newArr.map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    // 최종 updatedList는 객체 배열 형태로 저장됩니다.
    onSort(updatedList);
  };

  /**
   * 진행중 프로모션 리스트
   * --
   */
  const progressPromotion = React.useMemo(() => {
    let arr = promotionList;
    if (searchText && searchedColumn === 'processed_promotion_title') {
      arr = arr.filter((promotion) =>
        promotion.promotion_title.includes(searchText)
      );
    }
    return arr.filter((promotion) => {
      const now = dayjs();
      const start = dayjs.unix(promotion.promotion_start_date);
      const end = dayjs.unix(promotion.promotion_end_date);

      return now.isAfter(start) && now.isBefore(end);
    });
  }, [promotionList, searchText, searchedColumn]);

  /**
   * 만료된 프로모션 리스트
   * --
   */
  const expiredPromotion = React.useMemo(() => {
    let arr = promotionList;

    if (searchText && searchedColumn === 'expired_promotion_title') {
      arr = arr.filter((promotion) =>
        promotion.promotion_title.includes(searchText)
      );
    }
    return arr.filter((promotion) => {
      const now = dayjs();
      const end = dayjs.unix(promotion.promotion_end_date);
      return now.isAfter(end);
    });
  }, [promotionList, searchText, searchedColumn]);

  /* ===== HOOKS ===== */
  /**
   * 순서정렬 아이템 정제
   * --
   */
  React.useEffect(() => {
    if (!isOpened) return;
    if (modalType !== 'SORT') return;

    const filterItems = [...promotionList]?.sort((a, b) => a?.order - b?.order);

    onSort(filterItems);

    // eslint-disable-next-line
  }, [promotionList, modalType, isOpened]);
  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row>
            <Col x={24}>
              <Title style={{ ...styles.title }}>프로모션 목록</Title>

              {/* 요약 */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 40 }}
              >
                <Row gutter={20}>
                  <Col x={12}>
                    <Content
                      maxWidth={'100%'}
                      padding={0}
                      backgroundColor="none"
                    >
                      <div>
                        <div
                          style={{
                            padding: '10px 16px',
                            background: '#E1E2E4',
                            borderTopRightRadius: 4,
                            borderTopLeftRadius: 4,
                          }}
                        >
                          <h4 style={{ fontSize: 13, fontWeight: 500 }}>
                            진행중인 프로모션
                          </h4>
                        </div>
                        <div
                          style={{
                            borderBottomLeftRadius: 4,
                            borderBottomRightRadius: 4,
                            background: '#FFFFFF',
                          }}
                        >
                          <Table
                            minHeight={260}
                            layoutStyle={{ height: 400 }}
                            columns={sideColumns['PROCESS']}
                            dataSource={progressPromotion}
                          />
                        </div>
                      </div>
                    </Content>
                  </Col>
                  <Col x={12}>
                    <Content
                      maxWidth={'100%'}
                      padding={0}
                      backgroundColor="none"
                    >
                      <div>
                        <div
                          style={{
                            padding: '10px 16px',
                            background: '#E1E2E4',
                            borderTopRightRadius: 4,
                            borderTopLeftRadius: 4,
                          }}
                        >
                          <h4 style={{ fontSize: 13, fontWeight: 500 }}>
                            종료된 프로모션
                          </h4>
                        </div>
                        <div
                          style={{
                            borderBottomLeftRadius: 4,
                            borderBottomRightRadius: 4,
                            background: '#FFFFFF',
                          }}
                        >
                          <Table
                            minHeight={260}
                            layoutStyle={{ height: 400 }}
                            columns={sideColumns['EXPIRED']}
                            dataSource={expiredPromotion}
                          />
                        </div>
                      </div>
                    </Content>
                  </Col>
                </Row>
              </Content>
              {/* 요약 끝 */}

              {/* 검색필터 */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 40 }}
              >
                <ExpandableContainer borderRadius={4} padding={20}>
                  {formItem.map((_item, _index) => (
                    <div
                      key={`form_${_index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 0px',
                      }}
                    >
                      <div
                        style={{ width: 120, fontSize: 13, fontWeight: 400 }}
                      >
                        <label>{_item.label}</label>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {_item.form.map((_subitem, _subkey) => (
                          <React.Fragment key={`input_${_subkey}`}>
                            {_subitem.input}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </ExpandableContainer>

                {/* 검색/초기화 버튼 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 48,
                  }}
                >
                  <Button
                    size="large"
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={() => {
                      setIsLoading(true);
                      setPeriod({
                        start_date: dateValue.start_date.unix(),
                        end_date: dateValue.end_date.unix(),
                      });
                      setTimeout(() => {
                        setIsLoading(false);
                      }, 1000);
                    }}
                  >
                    검색
                  </Button>
                  <Button
                    size="large"
                    style={{ ...styles.button }}
                    onClick={() => {
                      setIsLoading(true);
                      setPeriod({
                        start_date: INITIAL_VALUE.start_date.unix(),
                        end_date: INITIAL_VALUE.end_date.unix(),
                      });
                      setDateValue(INITIAL_VALUE);
                      setTimeout(() => {
                        setIsLoading(false);
                      }, 1000);
                    }}
                  >
                    초기화
                  </Button>
                </div>
                {/* 검색/초기화 버튼 끝 */}
              </Content>
              {/* 검색필터 끝 */}

              {/* 검색결과 */}
              <Col x={24}>
                <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                  <Title
                    size={5}
                    subButton={
                      <div>
                        <Flex gap={5}>
                          <Button
                            danger
                            disabled={!selectedList?.length}
                            onClick={() => onDelete()}
                          >
                            {selectedList?.length}건 삭제
                          </Button>
                          <Button
                            disabled={!selectedList?.length}
                            onClick={() => onSubmit('is_show', 'Y')}
                          >
                            {selectedList?.length}건 노출
                          </Button>
                          <Button
                            disabled={!selectedList?.length}
                            onClick={() => onSubmit('is_show', 'N')}
                          >
                            {selectedList?.length}건 노출 안함
                          </Button>
                          <Button
                            disabled={!selectedList?.length}
                            onClick={() => onSubmit('is_main', 'Y')}
                          >
                            {selectedList?.length}건 메인 진열
                          </Button>
                          <Button
                            disabled={!selectedList?.length}
                            onClick={() => onSubmit('is_main', 'N')}
                          >
                            {selectedList?.length}건 메인 진열 안함
                          </Button>
                          <Button
                            onClick={() => {
                              setModalType('SORT');
                              onOpen(true);
                            }}
                          >
                            순서 변경
                          </Button>
                          <Button
                            style={{ marginRight: 5 }}
                            onClick={() =>
                              downloadXlsx({
                                columns: columns,
                                rows: promotionList?.map((_item) => ({
                                  ..._item,
                                  discount_rate:
                                    _item.discount_type === 'W'
                                      ? `${_item.discount_rate}원`
                                      : `${_item.discount_rate}%`,
                                  max_discount_price: `${_item?.max_discount_price}원`,
                                  reserve_rate:
                                    _item.reserve_type === 'W'
                                      ? `${_item.reserve_rate}원`
                                      : `${_item.reserve_rate}%`,
                                })),
                                fileName: '프로모션',
                              })
                            }
                          >
                            엑셀로 다운로드
                          </Button>
                        </Flex>
                      </div>
                    }
                  >
                    <span>
                      검색된 프로모션 총{' '}
                      <span style={{ color: 'var(--primary-color)' }}>
                        {promotionList?.length}
                      </span>
                      개
                    </span>
                  </Title>
                  <br />
                  {/* === 데이터 테이블 === */}
                  <Table
                    rowKey={'promotion_id'}
                    dataSource={promotionList}
                    scroll={{
                      x: 'max-content',
                    }}
                    columns={columns}
                    rowSelection={{
                      onChange: (_, rows) => onSelect(rows),
                    }}
                    onRow={(record) => ({
                      onClick: () => {
                        setModalType('UPDATE');
                        onOpen(true);
                        setModalData({ ...record, key: dayjs().unix() });
                      },
                    })}
                  />
                </Content>
              </Col>

              {/* 검색결과 끝 */}
            </Col>
          </Row>

          <ModalLayout
            type="modal"
            centered
            title={'프로모션 수정'}
            open={isOpened}
            onCancel={() => {
              onOpen(false);
              setModalData(null);
              onSort([]);
            }}
            closable
            width={modalType === 'UPDATE' ? '90%' : '30%'}
            footer={
              <Content
                maxWidth={'100%'}
                padding={0}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                {modalType === 'UPDATE' ? (
                  <PromotionRegisterContainer
                    data={modalData}
                    modalType={modalType}
                    onOpen={onOpen}
                    isOpened={isOpened}
                    currentShop={currentShop}
                  />
                ) : (
                  <Flex vertical gap={24} style={{ padding: '12px 0px' }}>
                    {sortItems?.map((_item, _index) => (
                      <Flex
                        key={`promotion-${_item.promotion_id}`}
                        justify="space-between"
                        align="center"
                        style={{ width: '500px' }}
                      >
                        <Flex align="center" gap={12}>
                          <div
                            style={{
                              width: 80,
                              height: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #BDBDBD',
                            }}
                          >
                            <Image
                              src={`${STORE_URL}/${_item?.thumbnail_url}`}
                              width={80}
                              height={'100%'}
                              style={{ objectFit: 'cover' }}
                            />
                          </div>
                          <Text style={{ flex: 1 }}>
                            {_item?.promotion_title}
                          </Text>
                        </Flex>

                        <Select
                          value={_index}
                          options={new Array(sortItems?.length)
                            .fill(null)
                            .map((_, index) => ({
                              label: `${index + 1}번`,
                              value: index,
                            }))}
                          onChange={(e) => orderChange(_item, Number(e))}
                        />
                      </Flex>
                    ))}
                    <Flex align="center" justify="center" gap={10}>
                      <Button
                        type="primary"
                        size="large"
                        onClick={() => onSubmit('sort', 'Y')}
                      >
                        저장
                      </Button>
                      <Button
                        type="primary"
                        size="large"
                        danger
                        onClick={() => {
                          onOpen(false);
                          onSort([]);
                        }}
                      >
                        취소
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </Content>
            }
          >
            <Content maxWidth={'100%'} padding={'30px 0px'}></Content>
          </ModalLayout>
        </Content>
      </Spin>
    </>
  );
};

export default PromotionListPresenter;
